// Scene cards for what the renderer draws itself (spec-v3-formats.md): math scenes show their equation steps (a
// readable preview of the LaTeX; the video sets it with KaTeX) and plot; kinetic "type" scenes show their big words;
// title and quote cards show their text. Picture scenes get their lower third and source chip as overlays.
import type { ReactNode } from "react";
import { compileFn, LATEX_SYMBOLS, latexPreview, plotPath, type Visual } from "../formats";
import type { AdScene } from "../types";

/** The visual a card can draw without a picture (math, type, title, quote). */
export const drawable = (v: Visual | undefined | null): v is Extract<Visual, { kind: "math" | "type" | "title" | "quote" }> => !!v && (v.kind === "math" || v.kind === "type" || v.kind === "title" || v.kind === "quote");

/** `size`: "full" in the preview, "thumb" in the filmstrip (fewer details). */
export function SceneVisual({ scene, look, size = "full" }: { scene: AdScene; look?: string; size?: "full" | "thumb" }) {
  const v = scene.visual;
  if (!drawable(v)) return null;
  const cls = `sv sv-${v.kind} sv-${size}${look ? ` sv-look-${look}` : ""}`;
  if (v.kind === "math") return <MathCard v={v} cls={cls} thumb={size === "thumb"} />;
  if (v.kind === "type") return <TypeCard words={scene.voiceover || scene.onScreenText} emphasis={v.emphasis} cls={cls} thumb={size === "thumb"} />;
  if (v.kind === "title")
    return (
      <div className={cls}>
        <p className="sv-title">{v.text}</p>
        {v.subtitle && size === "full" && <p className="sv-sub">{v.subtitle}</p>}
      </div>
    );
  return (
    <div className={cls}>
      <p className="sv-quote">“{v.text}”</p>
      {v.by && size === "full" && <p className="sv-by">{v.by}</p>}
    </div>
  );
}

// ------------------------------------------------------------------ a small LaTeX typesetter for the cards

type TexNode = string | { t: "frac" | "sup" | "sub" | "sqrt"; a: TexNode[]; b?: TexNode[] };

function texParse(s: string, i: number, close: string | null): [TexNode[], number] {
  const out: TexNode[] = [];
  const text = (c: string) => (typeof out[out.length - 1] === "string" ? (out[out.length - 1] = (out[out.length - 1] as string) + c) : out.push(c));
  while (i < s.length) {
    const c = s[i]!;
    if (close && c === close) return [out, i + 1];
    if (c === "{") {
      const [g, j] = texParse(s, i + 1, "}");
      out.push(...g);
      i = j;
    } else if (c === "^" || c === "_") {
      const [g, j] = texArg(s, i + 1);
      out.push({ t: c === "^" ? "sup" : "sub", a: g });
      i = j;
    } else if (c === "\\") {
      const m = /^\\([A-Za-z]+|.)/.exec(s.slice(i));
      const name = m?.[1] ?? "";
      i += m?.[0].length ?? 1;
      if (/^[A-Za-z]/.test(name) && s[i] === " ") i++;
      if (name === "frac" || name === "dfrac" || name === "tfrac") {
        const [a, j] = texArg(s, i);
        const [b, k] = texArg(s, j);
        out.push({ t: "frac", a, b });
        i = k;
      } else if (name === "sqrt") {
        const [a, j] = texArg(s, i);
        out.push({ t: "sqrt", a });
        i = j;
      } else if (name === "text" || name === "mathrm" || name === "mathbf" || name === "operatorname") {
        const [a, j] = texArg(s, i);
        out.push(...a);
        i = j;
      } else if (name === "\\") text("  ");
      else text(LATEX_SYMBOLS[name] ?? name);
    } else {
      text(c === "-" ? "−" : c);
      i++;
    }
  }
  return [out, i];
}
function texArg(s: string, i: number): [TexNode[], number] {
  while (s[i] === " ") i++;
  if (s[i] === "{") return texParse(s, i + 1, "}");
  if (s[i] === "\\") {
    const m = /^\\([A-Za-z]+|.)/.exec(s.slice(i));
    const [g] = texParse(m?.[0] ?? "", 0, null);
    return [g, i + (m?.[0].length ?? 1)];
  }
  return [[s[i] ?? ""], i + 1];
}
const spaced = (t: string) => t.replace(/\s*([=+−<>≤≥≈→×·])\s*/g, " $1 ").replace(/\s+/g, " ");
function texRender(nodes: TexNode[], key = "n"): ReactNode[] {
  return nodes.map((n, i) => {
    const k = `${key}${i}`;
    if (typeof n === "string") return spaced(n);
    if (n.t === "frac")
      return (
        <span key={k} className="mfrac">
          <span className="mfrac-a">{texRender(n.a, `${k}a`)}</span>
          <span className="mfrac-b">{texRender(n.b ?? [], `${k}b`)}</span>
        </span>
      );
    if (n.t === "sqrt")
      return (
        <span key={k} className="msqrt">
          √<span className="msqrt-a">{texRender(n.a, `${k}a`)}</span>
        </span>
      );
    const Tag = n.t;
    return <Tag key={k}>{texRender(n.a, `${k}a`)}</Tag>;
  });
}

/** LaTeX set for a card: stacked fractions, real super and subscripts, roots (the video uses KaTeX). */
export function Tex({ src }: { src: string }) {
  const [nodes] = texParse(src.replace(/\$+/g, "").trim(), 0, null);
  return (
    <span className="tex" aria-label={latexPreview(src)}>
      {texRender(nodes)}
    </span>
  );
}

function MathCard({ v, cls, thumb }: { v: Extract<Visual, { kind: "math" }>; cls: string; thumb: boolean }) {
  // a `replace` step swaps out the one before it, so the board shows what's left at the end
  const shown: string[] = [];
  for (const s of v.steps) {
    if (s.replace && shown.length) shown.pop();
    shown.push(s.latex);
  }
  const lines = thumb ? shown.slice(-1) : shown.slice(-4);
  const plot = v.plot && compileFn(v.plot.fn) ? v.plot : null;
  return (
    <div className={`${cls}${plot ? " has-plot" : ""}`}>
      {plot && (
        <svg className="sv-plot" viewBox="0 0 200 120" preserveAspectRatio="none" aria-hidden>
          {plot.yMin < 0 && plot.yMax > 0 && <line x1="0" x2="200" y1={120 - ((0 - plot.yMin) / (plot.yMax - plot.yMin)) * 120} y2={120 - ((0 - plot.yMin) / (plot.yMax - plot.yMin)) * 120} className="sv-axis" />}
          {plot.xMin < 0 && plot.xMax > 0 && <line y1="0" y2="120" x1={((0 - plot.xMin) / (plot.xMax - plot.xMin)) * 200} x2={((0 - plot.xMin) / (plot.xMax - plot.xMin)) * 200} className="sv-axis" />}
          <path d={plotPath(plot.fn, plot, 200, 120)} className="sv-curve" />
        </svg>
      )}
      <ol className="sv-steps" aria-label="Equation steps">
        {lines.map((l, i) => (
          <li key={i} className={i === lines.length - 1 ? "is-last" : ""}>
            <Tex src={l} />
          </li>
        ))}
      </ol>
      {!thumb && v.caption && <p className="sv-cap">{v.caption}</p>}
      {!thumb && plot?.label && <p className="sv-cap">{plot.label}</p>}
    </div>
  );
}

function TypeCard({ words, emphasis, cls, thumb }: { words: string; emphasis: string[]; cls: string; thumb: boolean }) {
  const all = words.split(/\s+/).filter(Boolean);
  const pick = thumb ? all.slice(0, 2) : all.slice(0, 7);
  const hot = new Set(emphasis.map((e) => e.toLowerCase().replace(/[^\p{L}\p{N}']/gu, "")));
  return (
    <div className={cls}>
      <p className="sv-words">
        {pick.map((w, i) => (
          <span key={i} className={hot.has(w.toLowerCase().replace(/[^\p{L}\p{N}']/gu, "")) ? "is-hot" : ""}>
            {w}{" "}
          </span>
        ))}
      </p>
    </div>
  );
}

/** A documentary's lower third and source chip over the picture. */
export function SceneBadges({ scene }: { scene: AdScene }) {
  if (!scene.lowerThird && !scene.source) return null;
  return (
    <span className="sbadges">
      {scene.lowerThird && (
        <span className="lower-third">
          <b>{scene.lowerThird.title}</b>
          {scene.lowerThird.subtitle && <span>{scene.lowerThird.subtitle}</span>}
        </span>
      )}
      {scene.source && <span className="source-chip">{scene.source.replace(/^source:\s*/i, "")}</span>}
    </span>
  );
}
